import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkUpdateTasks, completeTask, type Task, type TaskStatus } from '../api';
import { STATUS_KEYS, STATUS_VERB } from '../taskVocab';
import { useMedia, PHONE } from '../media';
import { BulkEdit } from './BulkEdit';
import { Icon, type IconName } from './Icon';
import { MenuSurface, useMenuAnchor, type MenuItem } from './MenuSurface';

/**
 * Every verb, in one place, acting on whatever is under the cursor.
 *
 * This replaces `TaskStatusControl`, which printed a checkbox, a `start` button and a `move…`
 * select on every row. Measured on the deployed build, `start` and `move…` together were 137
 * pixels of each 920-pixel row — against 158 for the task title itself — so the row spent almost
 * as much width on its verbs as on the thing it was naming. Ten rows meant ten copies of a menu
 * to serve an action taken on one task at a time.
 *
 * The verbs did not get less reachable by moving here; they got more. A `<select>` cost two
 * interactions and a three-option menu read. These are one click, or one keystroke, and they apply
 * to a multi-selection for free — which is the difference between seven stale tasks being seven
 * decisions and being one.
 */

/** One icon per transition. The key and the verb come from `taskVocab`; this is the drawing. */
const ICONS: Record<TaskStatus, IconName> = {
  in_progress: 'start',
  next: 'next',
  inbox: 'inbox',
  done: 'done',
  dropped: 'drop',
  parked: 'drop',
};

/**
 * The commands, in the order somebody reaches for them. Keys match `TASK_KEYS` in `App`.
 *
 * The words come from `STATUS_VERB`, not from here — this row read `Start | Next | Inbox | Done |
 * Drop`, five buttons in three parts of speech, and the row menu and the two detail surfaces each
 * had their own opinion about what the last-but-one was called.
 *
 * And now the *keys* come from `STATUS_KEYS` for the same kind of reason, so this file holds no
 * binding of its own. It used to, and `keys.ts` kept a hand-written copy of four of the same
 * five — which is how `#` came to be bound here and absent from the registry that feeds the
 * keyboard help, the one state that file's header says must never hold.
 * `keysAreListed.spec.ts` is what keeps the two from parting again; composing them is what makes
 * them one list.
 */
export const COMMANDS: {
  status: TaskStatus;
  icon: IconName;
  label: string;
  key: string;
}[] = STATUS_KEYS.map(({ status, key }) => ({
  status,
  key,
  icon: ICONS[status],
  label: STATUS_VERB[status].verb,
}));

/**
 * Completion routes through `completeTask`, never a status write.
 *
 * Phase 36 made the two equivalent — a recurring task's successor, the habit entry, the block all
 * hang off completion — and routing through the named one keeps them honest if they ever drift.
 */
export function useTaskCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: readonly string[]; status: TaskStatus }) => {
      /*
        Completion is the one verb that still goes task by task.

        Completing a task runs its recurring successor, its habit entry and its timeline block, all
        of which live inside `completeTask`. The bulk endpoint refuses `done` outright rather than
        writing the row and skipping the rest — a batched completion that only moved the status
        would be silently wrong in five places at once.

        Everything else is one request. Seven selected tasks used to be seven round trips, each
        invalidating a list the next one was about to refetch.
      */
      if (status === 'done') {
        for (const id of ids) await completeTask(id);
        return;
      }
      await bulkUpdateTasks([...ids], { status });
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function TaskCommandBar({
  targets,
  selectedCount,
  hiddenCount = 0,
  allOf = null,
  onClearSelection,
  /** The one selected task, when exactly one is selected — the bar names it and hides its dead verbs. Never the cursor's row: see `soleSelectedTask`. */
  task,
  projectId,
  onCommitted,
  onDescribeEdit,
}: {
  targets: string[];
  selectedCount: number;
  /**
   * How many of those the current filter is not drawing.
   *
   * The bar has to name this or the count becomes a lie the moment a filter narrows. Ticking
   * "Unfiled" over twelve selected rows used to leave the bar gone and the selection empty;
   * keeping the twelve instead is only defensible while the bar says plainly that four of them are
   * off screen and that the verbs beside it will act on all twelve.
   */
  hiddenCount?: number;
  /**
   * What the selection *is*, when it is everything the filter matched.
   *
   * "10 selected" and "All 10 blocked selected" are different claims, and only the second answers
   * the question somebody asks after pressing a select-all: whether the batch about to be acted on
   * is the set they narrowed to. The caller names the filter because the caller owns the chips
   * that spell it; absent, the bar says the plain count, which is still true.
   */
  allOf?: string | null;
  onClearSelection: () => void;
  task: Task | null;
  /*
    Told what just happened, so the list can print the readback and offer the way back.

    The readback and its Reopen already existed and were wired to the keyboard path alone — `x`
    named what it had done while the bar beside it, six one-click unconfirmed whole-selection
    verbs, said nothing. Reported upwards rather than rendered here, because the list owns the
    `before` snapshot that Reopen needs and this component only knows the ids.
  */
  onCommitted?: (status: Task['status']) => void;
  /* What a bulk edit changed, so the list can say it — see `onDescribe` in `BulkEdit`. */
  onDescribeEdit?: (what: string, count: number) => void;
  /**
   * The project the list is scoped to, if any — the only context in which assigning means anything.
   *
   * A task outside a project can only be held by its own author (`setAssignee` refuses anything
   * else), so there is nobody to choose between. Scoped to a project there is a member list, which
   * is what bulk edit needs to offer.
   */
  projectId?: string | null;
}) {
  const command = useTaskCommand();
  const disabled = targets.length === 0 || command.isPending;
  const phone = useMedia(PHONE);
  const more = useMenuAnchor<null>();
  const openAtPress = useRef(false);

  const run = (status: TaskStatus) => {
    // Before the mutation: `targets` is about to be cleared and the rows about to leave.
    onCommitted?.(status);
    command.mutate({ ids: targets, status });
  };

  const offered = COMMANDS.filter((c) => {
    /*
      A verb pointing at the state the task is already in is not disabled, it is absent.
      A greyed-out button still costs a scan; on a multi-selection there is no single current
      state, so everything stays offered.
    */
    if (task && selectedCount <= 1 && task.status === c.status) return false;
    // Starting something blocked by another task is the one genuinely invalid move.
    if (c.status === 'in_progress' && task?.isBlocked) return false;
    return true;
  }).map((c) => {
    /*
      The one verb here that destroys, dressed as one — and saying how much.

      All six read `rgb(28,28,26)` at weight 400, identical to the last decimal: `Drop` was
      byte-identical to `To next`, and the only irreversible verb on the row was the one nothing
      distinguished. The count rides on it for the same reason: a destructive verb that does not
      say how much it will take is the one place a wrong selection costs everything in one press
      (`01a08097-0e4c`).

      Only on a multiple selection. "Drop 1" beside a single row is noise, and the row is right
      there.

      *Complete as the single filled primary* — the reviewer's other suggestion — is deliberately
      not done: the fill is reserved for a form's one commit control and a selection bar has none.
    */
    const destructive = c.status === 'dropped';
    const label = destructive && selectedCount > 1 ? `${c.label} ${selectedCount}` : c.label;
    return { ...c, destructive, label };
  });

  /*
    On a phone the bar is one row: the count, Complete, Edit… and a More holding the rest.

    All six verbs and Edit… wrapped to three lines at 375 — measured 161px of an 812px screen with
    two rows selected, sticky, so those three lines rode over the list the whole way down and left
    two and a half rows visible (`01a08097-0a1d`, reported on 1.39.0 and again on 1.70.0). Complete
    stays because it is the transition the app treats as most frequent — the row carries its own
    box for exactly that — and Edit… because it is the one door to every other field. Which two
    stay is *inferred from the code, not from usage*; a round that reports Drop or To next as lost
    on a phone is what reopens it.

    A menu rather than icon-only buttons or a sideways-scrolling row: six unlabelled glyphs for six
    status verbs is a legend to learn, and a row that scrolls hides verbs without saying so. And at
    the top under the header, not anchored to the bottom: the app has no bottom chrome, the phone
    browser has its own there, and the verbs would sit at the far end of the screen from the rows
    they act on.

    Decided in JS rather than by hiding buttons in CSS, so a folded verb is not also a hidden
    duplicate on the bar — one control per verb at any width, the same reason `PHONE` exists.
  */
  const onBar = phone ? offered.filter((c) => c.status === 'done') : offered;
  const folded = phone ? offered.filter((c) => c.status !== 'done') : [];
  const foldedItems: MenuItem[] = folded.map((c) => ({
    key: c.status,
    icon: c.icon,
    label: c.label,
    danger: c.destructive,
    run: () => {
      more.close();
      run(c.status);
    },
  }));

  /* The scope prints a title rather than a count; at phone width that changes how it may shrink (see `.command-scope.is-title`). */
  const titled = !(selectedCount > 1 || hiddenCount > 0);

  return (
    <div className="command-bar" role="toolbar" aria-label="Task actions">
      <span className={titled ? 'command-scope is-title' : 'command-scope'}>
        {/* One hidden row is enough to need the count: "Nothing selected" beside a live selection
            is the same silence this bar exists to break. */}
        {!titled ? (
          <>
            {/*
              "All 10 blocked selected" when that is what it is.

              The count alone leaves the reader to work out whether they got the whole filtered set
              — which is exactly what they pressed the box to find out — and it is the sentence the
              finding asked for by name.
            */}
            {allOf === null ? (
              <>
                <strong>{selectedCount}</strong> selected
              </>
            ) : (
              <>
                All <strong>{selectedCount}</strong> {allOf} selected
              </>
            )}
            {hiddenCount > 0 && (
              <span className="subtext"> · {hiddenCount} hidden by the filter, still selected</span>
            )}
            <button type="button" className="link" onClick={onClearSelection}>
              clear
            </button>
          </>
        ) : (
          <span className="subtext">{task ? task.title : 'Nothing selected'}</span>
        )}
      </span>

      {onBar.map((c) => (
        <button
          key={c.status}
          type="button"
          className={c.destructive ? 'command danger' : 'command'}
          disabled={disabled}
          title={`${c.label} — ${c.key}`}
          aria-label={c.label}
          aria-keyshortcuts={c.key}
          onClick={() => run(c.status)}
        >
          <Icon name={c.icon} />
          {/* `.command` is a flex row with a gap, so the label needs no element of its own —
              and the class it used to carry existed only for a rule that hid it on phones,
              which is the rule this replaced. */}
          {c.label}
        </button>
      ))}

      <BulkEdit
        targets={targets}
        onApplied={onClearSelection}
        onDescribe={onDescribeEdit}
        projectId={projectId ?? null}
      />

      {foldedItems.length > 0 && (
        <>
          <button
            type="button"
            className="command command-more"
            disabled={disabled}
            aria-label="More actions"
            title="More actions"
            aria-haspopup="menu"
            aria-expanded={more.state !== null}
            /*
              A second tap on the trigger closes the menu rather than reopening it. The menu's own
              outside-pointerdown dismissal fires before this click and would otherwise leave the
              click to open it again; the root's listener runs before the document's, so here the
              menu is still known to be open.
            */
            onPointerDown={() => {
              openAtPress.current = more.state !== null;
            }}
            onClick={(event) => {
              if (openAtPress.current) more.close();
              else more.openUnder(event, null);
              openAtPress.current = false;
            }}
          >
            {/*
              The three dots, alone: the glyph every row menu here already uses for "everything else
              this can do". The word cost ~35px the one-row bar did not have at 375 — the arithmetic
              is in the commit — and this is one trigger, not the six unlabelled verbs the decision
              ruled out. Named for assistive tech and on hover.
            */}
            <Icon name="more" />
          </button>
          {/*
            `MenuSurface` says nothing built on it is reachable *only* from it. For the bulk form of
            these four verbs on a phone, that is now nearly the case: one task can still be moved
            from its panel and a keyboard still has the keys, but a touch-only selection has this
            menu. Its items are ordinary buttons in DOM order right after the trigger, so a screen
            reader and Tab both reach them once it is open — the bar the shell's note sets.
          */}
          <MenuSurface
            anchor={more.state?.anchor ?? null}
            label={
              selectedCount > 1 ? `More actions for ${selectedCount} selected` : 'More actions'
            }
            items={foldedItems}
            onClose={more.close}
          />
        </>
      )}
    </div>
  );
}
